import { useCallback, useEffect, useState } from "react";
import Papa from "papaparse";
import PropTypes from "prop-types";

import { strCode } from "../../consts";
import { log, notificationService, t } from "../../services";

import LoadingModal from "../LoadingModal/LoadingModal";
import Modal from "../Modal/Modal";
import WrapperContainer from "../Containers/WrapperContainer";
import FlexContainers from "../Containers/FlexContainers";

import DragAndDropInput from "./components/DragAndDropInput/DragAndDropInput";
import TableExample from "./components/TableExample/TableExample";
import ImportingSummary from "./components/ImportingSummary/ImportingSummary";

const BatchCreate = ({
  active,
  setActive,
  csvExampleName,
  csvExampleUrl,
  successInfo,
  failedInfo,
  onSubmit,
  formatParsedData,
  formatRecords,
  ImportedRecordsTableColumns,
  SuccessImportActions
}) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [active]);

  const onParseComplete = useCallback(
    (results) => {
      if (results?.data?.length) {
        setData(formatParsedData(results?.data));
        setActive(false);
      } else {
        setError(true);
        notificationService.showError({
          title: t(strCode.EMPTY_FILE_ERROR_TITLE),
          message: t(strCode.EMPTY_FILE_ERROR_MESSAGE)
        });
      }

      setLoading(false);
    },
    [setData, setActive, setError]
  );

  const parseData = useCallback(
    async (file) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: onParseComplete,
        error: () => {
          notificationService.showError({
            title: t(strCode.FILE_IMPORT_ERROR_TITLE),
            message: t(strCode.FILE_IMPORT_ERROR_MESSAGE)
          });
          setError(true);
          setLoading(false);
        }
      });
    },
    [onParseComplete]
  );

  const onUploadFile = async (uploadedFile) => {
    setLoading(true);
    await parseData(uploadedFile);
  };

  const onSubmitFile = async (file, correctRecords) => {
    setLoading(true);

    try {
      await onSubmit(file, correctRecords);

      setData();
    } catch (e) {
      log.error(`An error occurred while submitting .csv file.`, e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingModal id={"loading-modal"} active={loading} text={t(strCode.IMPORTING_INFO)} />

      <Modal
        id={"batch-create-modal"}
        active={active && !loading}
        setActive={setActive}
        title={t(strCode.BATCH_CREATE_BTN)}
        closable>
        <WrapperContainer Container={FlexContainers.FlexColumn} width={446} gap={24}>
          <DragAndDropInput onUploadFile={onUploadFile} error={error} setError={setError} />
          {csvExampleName && csvExampleUrl && (
            <TableExample fileName={csvExampleName} fileUrl={csvExampleUrl} />
          )}
        </WrapperContainer>
      </Modal>

      <ImportingSummary
        active={!!data && !loading}
        onClose={() => setData()}
        data={data}
        successInfo={successInfo}
        failedInfo={failedInfo}
        formatRecords={formatRecords}
        onSubmitFile={onSubmitFile}
        ImportedRecordsTableColumns={ImportedRecordsTableColumns}
        SuccessImportActions={SuccessImportActions}
      />
    </>
  );
};

BatchCreate.propTypes = {
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  csvExampleName: PropTypes.string,
  csvExampleUrl: PropTypes.string,
  successInfo: PropTypes.string.isRequired,
  failedInfo: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formatParsedData: PropTypes.func.isRequired,
  formatRecords: PropTypes.func.isRequired,
  ImportedRecordsTableColumns: PropTypes.oneOfType([PropTypes.node, PropTypes.object]).isRequired,
  SuccessImportActions: PropTypes.oneOfType([PropTypes.node, PropTypes.object])
};

export default BatchCreate;
